import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, DollarSign, FlaskConical, Newspaper, Radio, Settings, Zap } from 'lucide-react';
import DemoScenarioItems from './DemoScenarioItems';
import ModeIndicator from './ModeIndicator';
import UserMenu from './UserMenu';
import { APPEARANCES, useAppearance } from './appearance';
import { ROUTES } from './paths';
import { SECTIONS, type SectionId } from './sections';
import { useUtcClock } from './useUtcClock';

interface TopBarProps {
  /** Who is signed in, as shown in the account menu. */
  userLabel: string;
  demoMode: boolean;
  /** The console section in view, or null off the console (nothing is underlined). */
  activeSection: SectionId | null;
  /** How many incidents await a decision; shown on Threats when there are some. */
  pending?: number;
  /** The logo for a dark surface (Normal mode, and the views that always render in it). */
  onDark: boolean;
  onSection: (id: SectionId) => void;
  onOpenIntel: () => void;
  onRequestSignOut: () => void;
  /** The demo scenarios, offered in Demo only (the Demo chip's menu, and the account menu at 1200 px and below). */
  onStorm: () => void;
  onNeedle: () => void;
  onReset: () => void;
  /** A storm is running. */
  storming: boolean;
}

/**
 * The fixed 60 px bar on every view (items 9, 42, 43, 52, 62). Items that do not fit move into the
 * account menu at 1200 px (`c3-hide-le1200`) and 1000 px (`c3-hide-le1000`); the matching
 * `c3-show-le*` entries in that menu appear at the same widths. At 660 px the section links lose
 * their labels (see c3.css). Search is not here: it lives in the Threat Feed (client notes 10/26, items 2 and 3).
 */
const TopBar: React.FC<TopBarProps> = ({
  userLabel,
  demoMode,
  activeSection,
  pending = 0,
  onDark,
  onSection,
  onOpenIntel,
  onRequestSignOut,
  onStorm,
  onNeedle,
  onReset,
  storming,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const clock = useUtcClock();
  const { appearance, setAppearance } = useAppearance();
  const onSettingsPage = pathname === ROUTES.settings || pathname.startsWith(`${ROUTES.settings}/`);

  return (
    <header className="c3-topbar">
      <Link to={ROUTES.console} className="c3-logo">
        <img src={onDark ? '/logo.svg' : '/logo-ink.svg'} alt="Arbitium" />
      </Link>

      <nav className="c3-topbar-nav" aria-label="Sections">
        {SECTIONS.map(({ id, label, icon: Icon }) => {
          const current = activeSection === id;
          const waiting = id === 'respond' ? pending : 0;
          return (
            <button
              key={id}
              type="button"
              className={`c3-topbar-link${current ? ' is-active' : ''}`}
              aria-current={current ? 'location' : undefined}
              aria-label={waiting > 0 ? `${label}, ${waiting} awaiting your decision` : label}
              title={label}
              onClick={() => onSection(id)}
            >
              <Icon size={16} strokeWidth={1.5} aria-hidden />
              <span className="c3-topbar-link-label">{label}</span>
              {waiting > 0 && (
                <span className="c3-topbar-count" aria-hidden>
                  {waiting}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="c3-topbar-end">
        <div className="c3-hide-le1200">
          <ModeIndicator
            demo={demoMode}
            onStorm={onStorm}
            onNeedle={onNeedle}
            onReset={onReset}
            storming={storming}
          />
        </div>
        <button
          type="button"
          className="c3-topbar-icon has-label c3-hide-le1000"
          aria-label="Threat Intel"
          title="Threat Intel"
          aria-haspopup="dialog"
          onClick={onOpenIntel}
        >
          <Newspaper size={16} strokeWidth={1.5} aria-hidden />
          <span className="c3-topbar-icon-label">Threat Intel</span>
        </button>
        <button
          type="button"
          className="c3-topbar-icon has-label c3-hide-le1000"
          aria-label="ROI"
          title="ROI"
          aria-current={pathname === ROUTES.roi ? 'page' : undefined}
          onClick={() => navigate(ROUTES.roi)}
        >
          <DollarSign size={16} strokeWidth={1.5} aria-hidden />
          <span className="c3-topbar-icon-label">ROI</span>
        </button>

        <button
          type="button"
          className="c3-topbar-icon has-label"
          aria-label="Settings"
          title="Settings"
          aria-current={onSettingsPage ? 'page' : undefined}
          onClick={() => navigate(ROUTES.settings)}
        >
          <Settings size={16} strokeWidth={1.5} aria-hidden />
          <span className="c3-topbar-icon-label">Settings</span>
        </button>

        <UserMenu
          label={userLabel}
          onRequestSignOut={onRequestSignOut}
          info={
            <div className="c3-user-info c3-show-le1200">
              <span className="inline-flex items-center gap-2">
                {storming ? (
                  <Zap size={16} strokeWidth={1.5} aria-hidden />
                ) : demoMode ? (
                  <FlaskConical size={16} strokeWidth={1.5} aria-hidden />
                ) : (
                  <Radio size={16} strokeWidth={1.5} aria-hidden />
                )}
                {storming ? 'Storm in progress…' : demoMode ? 'Demo' : 'Live'}
              </span>
              <span className="c3-mode-time">{clock} UTC</span>
            </div>
          }
        >
          {demoMode && (
            <>
              <DemoScenarioItems
                className="c3-show-le1200"
                onStorm={onStorm}
                onNeedle={onNeedle}
                onReset={onReset}
                storming={storming}
              />
              <div className="c3-nav-menu-sep c3-show-le1200" role="separator" />
            </>
          )}
          <button
            type="button"
            className="c3-nav-menu-item c3-show-le1000"
            role="menuitem"
            onClick={onOpenIntel}
          >
            <span className="inline-flex items-center gap-2">
              <Newspaper size={16} strokeWidth={1.5} aria-hidden />
              Threat Intel
            </span>
          </button>
          <button
            type="button"
            className="c3-nav-menu-item c3-show-le1000"
            role="menuitem"
            onClick={() => navigate(ROUTES.roi)}
          >
            <span className="inline-flex items-center gap-2">
              <DollarSign size={16} strokeWidth={1.5} aria-hidden />
              ROI
            </span>
          </button>
          <div className="c3-nav-menu-sep" role="separator" />
          <div role="group" aria-labelledby="c3-appearance-heading">
            <div className="c3-nav-menu-heading" id="c3-appearance-heading">
              Appearance
            </div>
            {APPEARANCES.map(({ id, label, hint }) => (
              <button
                key={id}
                type="button"
                className="c3-nav-menu-item"
                role="menuitemradio"
                aria-checked={appearance === id}
                onClick={() => setAppearance(id)}
              >
                <span>
                  {label} <span className="c3-nav-menu-sub">· {hint}</span>
                </span>
                {appearance === id && <Check size={16} strokeWidth={1.5} aria-hidden />}
              </button>
            ))}
          </div>
        </UserMenu>
      </div>
    </header>
  );
};

export default TopBar;
